'use client';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import { usePathname } from 'next/navigation';
import {
  FileText,
  Sun,
  Wind,
  Cloud,
  AlertTriangle,
  Printer,
  TrendingUp,
  TrendingDown,
  Minus,
  MapPin,
  Sunrise,
  Sunset,
  Thermometer,
  Zap,
  Gauge,
  Eye,
  Droplets,
  CloudRain,
} from 'lucide-react';
import { useAerodrome } from '@/context/AerodromeContext';
import { fetchSoaringForecast } from '@/lib/openMeteoApi';
import { MOCK_TODAY_FORECAST, MOCK_WEEK_FORECAST, DailySoaringForecast, AerodromeInfo } from '@/lib/mockWeatherData';

export default function DailyBriefingPage() {
  const pathname = usePathname();
  return (
    <AppLayout currentPath={pathname}>
      <DailyBriefingContent />
    </AppLayout>
  );
}

function ftToM(ft: number): number {
  return Math.round(ft * 0.3048);
}
function mToFt(m: number): number {
  return Math.round(m * 3.28084);
}

function verdictLabel(v: DailySoaringForecast['verdict']): string {
  switch (v) {
    case 'favorable': return 'favorable';
    case 'marginal': return 'marginal';
    case 'unflyable': return 'défavorable';
    case 'storm-risk': return 'risque orageux';
    default: return v;
  }
}

function ceilingTrend(a: DailySoaringForecast, b: DailySoaringForecast): 'hausse' | 'baisse' | 'stable' {
  const diff = b.maxCumulusBase - a.maxCumulusBase;
  if (diff > 150) return 'hausse';
  if (diff < -150) return 'baisse';
  return 'stable';
}

function conditionTrend(a: DailySoaringForecast, b: DailySoaringForecast): 'maintien' | 'amélioration' | 'dégradation' {
  const diff = b.qualityScore - a.qualityScore;
  if (diff > 10) return 'amélioration';
  if (diff < -10) return 'dégradation';
  return 'maintien';
}

function TrendIcon({ trend }: { trend: 'hausse' | 'baisse' | 'stable' | 'maintien' | 'amélioration' | 'dégradation' }) {
  if (trend === 'hausse' || trend === 'amélioration') return <TrendingUp size={14} style={{ color: '#34d399' }} />;
  if (trend === 'baisse' || trend === 'dégradation') return <TrendingDown size={14} style={{ color: '#f87171' }} />;
  return <Minus size={14} style={{ color: '#fbbf24' }} />;
}

/** Derive a pressure tendency index from convective proxies. This is not an official QNH. */
function deriveQNH(forecast: DailySoaringForecast): number {
  // Base QNH 1013 hPa, adjusted by LI (negative LI = lower pressure)
  const liAdjust = (forecast.li ?? 0) * 1.5;
  const capeAdjust = (forecast.cape ?? 0) > 200 ? -2 : 0;
  return Math.round(1013 + liAdjust + capeAdjust);
}

/** Derive estimated visibility from cloud cover and storm risk */
function deriveVisibility(forecast: DailySoaringForecast): number {
  const hourly = forecast.hourly ?? [];
  if (hourly.length === 0) return 30;
  const avgVis = hourly.reduce((s, h) => s + (h.visibility ?? 30), 0) / hourly.length;
  const stormPenalty = forecast.stormRisk > 40 ? -8 : forecast.stormRisk > 20 ? -3 : 0;
  return Math.max(5, Math.round(avgVis + stormPenalty));
}

/** Derive average dew point from hourly data */
function deriveDewPoint(forecast: DailySoaringForecast): number {
  const hourly = forecast.hourly ?? [];
  if (hourly.length === 0) return Math.round(forecast.tempMin - 8);
  const convHours = hourly.filter((h) => h.isConvective);
  const src = convHours.length > 0 ? convHours : hourly;
  return Math.round(src.reduce((s, h) => s + (h.dewpoint ?? h.temp - 8), 0) / src.length);
}

/** Compute spread (T - Td) to estimate cumulus formation probability */
function spreadLabel(temp: number, dewpoint: number): string {
  const spread = temp - dewpoint;
  if (spread < 4) return 'Faible (< 4°C) — nébulosité soudée probable, étalement précoce';
  if (spread < 8) return `Modéré (${spread.toFixed(0)}°C) — cumulus bien formés attendus`;
  return `Élevé (${spread.toFixed(0)}°C) — ciel bleu probable, thermiques secs`;
}

/** Generate causal pressure analysis sentence */
function pressureAnalysis(qnh: number, forecast: DailySoaringForecast): string {
  if (qnh < 1005) {
    return `Indice pression bas (${qnh}) - tendance instable possible; a confirmer avec une source meteo officielle.`;
  }
  if (qnh < 1010) {
    return `Indice pression en baisse (${qnh}) - signal de degradation possible, non assimilable a un QNH officiel.`;
  }
  if (qnh > 1020) {
    return `Indice pression eleve (${qnh}) - tendance plus stable probable; a valider avec la meteo officielle.`;
  }
  return `Indice pression nominal (${qnh}) - tendance neutre; valeur indicative seulement.`;
}

/** Generate causal humidity analysis sentence */
function humidityAnalysis(dewpoint: number, temp: number): string {
  const spread = temp - dewpoint;
  if (spread < 4) {
    return `Hausse de l'humidité en basse couche (Td ${dewpoint}°C, écart ${spread.toFixed(0)}°C) — risque d'étalement précoce ou de nébulosité soudée limitant l'activité thermique.`;
  }
  if (spread < 7) {
    return `Humidité modérée (Td ${dewpoint}°C, écart ${spread.toFixed(0)}°C) — formation de cumulus bien définis attendue en phase convective.`;
  }
  return `Humidité basse (Td ${dewpoint}°C, écart ${spread.toFixed(0)}°C) — masse d'air sèche, thermiques secs et plafond élevé favorisés.`;
}

function rainProbability(forecast: DailySoaringForecast): number {
  return forecast.precipProbabilityMax
    ?? Math.max(0, ...(forecast.hourly ?? []).map((h) => h.precipProbability ?? 0));
}

function rainTotalMm(forecast: DailySoaringForecast): number {
  return forecast.precipTotalMm
    ?? Math.round((forecast.hourly ?? []).reduce((sum, h) => sum + (h.precipAmount ?? 0), 0) * 10) / 10;
}

function rainAnalysis(forecast: DailySoaringForecast): string {
  const probability = rainProbability(forecast);
  const totalMm = rainTotalMm(forecast);
  const label = forecast.rainRiskLabel ?? (probability >= 35 ? 'Averses possibles' : 'Pas de pluie significative');
  const windowLabel = forecast.rainWindowLabel ?? 'Pas de pluie detectee pendant la fenetre de vol';

  if (totalMm >= 5 || probability >= 70) {
    return `${label} (${probability}%, ${totalMm.toFixed(1)} mm). ${windowLabel}. La pluie devient un facteur limitant distinct du risque orageux.`;
  }
  if (totalMm >= 1 || probability >= 35) {
    return `${label} (${probability}%, ${totalMm.toFixed(1)} mm). ${windowLabel}. A surveiller pour le depart et le retour terrain.`;
  }
  return `Pas de pluie significative prevue (${probability}%, ${totalMm.toFixed(1)} mm). ${windowLabel}.`;
}

function rainSummarySentence(forecast: DailySoaringForecast): string {
  const probability = rainProbability(forecast);
  const totalMm = rainTotalMm(forecast);
  if (totalMm >= 5 || probability >= 70) return ` Pluie probable (${probability}%, ${totalMm.toFixed(1)} mm).`;
  if (totalMm >= 1 || probability >= 35) return ` Averses possibles (${probability}%, ${totalMm.toFixed(1)} mm).`;
  return '';
}

function cleanBriefingText(text: string): string {
  const seen = new Set<string>();
  return text
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => {
      const key = sentence.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Generate causal wind analysis sentence */
function windAnalysis(forecast: DailySoaringForecast): string {
  const dir = forecast.windSurface.dir.toUpperCase();
  const speed = forecast.windSurface.speed;
  if (dir.includes('N') && !dir.includes('S')) {
    return `Bascule du vent au secteur Nord (${speed} km/h) — apport d'une masse d'air plus froide et sèche, propice à de meilleurs thermiques et un plafond plus élevé.`;
  }
  if (dir.includes('S') && !dir.includes('N')) {
    return `Flux de Sud (${speed} km/h) — masse d'air plus chaude et humide, thermiques actifs mais risque de surdéveloppement en fin de journée.`;
  }
  if (dir.includes('O') || dir.includes('W')) {
    return `Vent d'Ouest (${speed} km/h) - conditions changeantes possibles; surveiller l'evolution locale.`;
  }
  return `Vent de secteur ${dir} à ${speed} km/h — conditions de vent modérées, impact limité sur la convection.`;
}

/** Generate an indicative degradation sentence. This does not detect real synoptic fronts. */
function frontPassageAnalysis(forecast: DailySoaringForecast, nextDays: DailySoaringForecast[]): string | null {
  if (nextDays.length === 0) return null;
  const tomorrow = nextDays[0];
  const drop = forecast.qualityScore - tomorrow.qualityScore;
  if (drop > 25) {
    const impactHour = forecast.convectionEnd !== '--' ? forecast.convectionEnd : '18:00';
    return `Signal indicatif de degradation vers ${impactHour}. Baisse marquee attendue pour ${tomorrow.dayLabel}; a confirmer avec une analyse synoptique.`;
  }
  if (tomorrow.qualityScore > forecast.qualityScore + 20) {
    return `Signal indicatif d'amelioration pour ${tomorrow.dayLabel}, avec un potentiel de plafond en hausse.`;
  }
  return null;
}

/** Detect thermal type: pure thermals vs cumulus */
function thermalType(forecast: DailySoaringForecast): 'bleu' | 'cumulus' | 'aucun' {
  if (forecast.verdict === 'unflyable') return 'aucun';
  const dewDay = deriveDewPoint(forecast);
  const spread = forecast.tempMax - dewDay;
  if (spread >= 8) return 'bleu';
  return 'cumulus';
}

/** Detect scenario type for a given day */
type ScenarioType = 'belle-journee' | 'front-arrivant' | 'post-frontal' | 'mediocre' | 'neutre' | 'hangar';

function detectScenario(
  day: DailySoaringForecast,
  prevDay: DailySoaringForecast,
  nextDay: DailySoaringForecast | null
): ScenarioType {
  // Hard stop: score < 15 → hangar day (recalibrated v1.2.0 — score 0 is now rarer)
  if (day.qualityScore < 15) return 'hangar';

  const dewDay = deriveDewPoint(day);
  const spread = day.tempMax - dewDay;

  // Post-frontal: preceded by bad day, now improving with instability
  if (prevDay.verdict === 'unflyable' || prevDay.verdict === 'storm-risk') {
    if (day.qualityScore >= 45) return 'post-frontal';
  }
  // Degradation signal: humidity, storm risk, or a much worse next day.
  if (day.stormRisk > 35 && spread < 6) return 'front-arrivant';
  if (nextDay && day.qualityScore - nextDay.qualityScore > 20) return 'front-arrivant';
  // Belle journée: high score, good spread, low storm risk
  if (day.qualityScore >= 65 && spread >= 6 && day.stormRisk < 30) return 'belle-journee';
  // Mediocre
  if (day.qualityScore < 45) return 'mediocre';
  return 'neutre';
}

/** Get scenario badge label and colors */
function scenarioBadge(scenario: ScenarioType): { label: string; bg: string; color: string } {
  switch (scenario) {
    case 'belle-journee': return { label: 'Belle journée', bg: 'rgba(52,211,153,0.15)', color: '#34d399' };
    case 'front-arrivant': return { label: 'Degradation possible', bg: 'rgba(249,115,22,0.15)', color: '#f97316' };
    case 'post-frontal': return { label: 'Amelioration', bg: 'rgba(126,200,227,0.15)', color: '#7ec8e3' };
    case 'mediocre': return { label: 'Médiocre', bg: 'rgba(251,191,36,0.15)', color: '#fbbf24' };
    case 'hangar': return { label: 'Journée hangar', bg: 'rgba(148,163,184,0.15)', color: '#94a3b8' };
    default: return { label: 'Neutre', bg: 'rgba(100,116,139,0.15)', color: '#64748b' };
  }
}

/** Generate expert narrative for a day card */
function generateExpertNarrative(
  day: DailySoaringForecast,
  ref: DailySoaringForecast,
  nextDay: DailySoaringForecast | null,
  isBestDay: boolean,
  allDays: DailySoaringForecast[],
  aerodrome?: AerodromeInfo
): string {
  const scenario = detectScenario(day, ref, nextDay);
  const dewDay = deriveDewPoint(day);
  const spread = day.tempMax - dewDay;
  const thermal = thermalType(day);
  const sky = skyDescription(day);
  const terrainDesc = aerodrome ? getTerrainDescriptor(aerodrome) : '';
  const geoCtx = aerodrome ? geoContextSentence(aerodrome, day, allDays) : '';
  const regional = aerodrome ? regionalComparisonSentence(aerodrome, day, allDays) : '';

  if (scenario === 'hangar') {
    return aerodrome ? humanizedHangarMessage(day, aerodrome) : `Journée non propice. [${hangarCause(day)}]. Conditions incompatibles avec le vol à voile.`;
  }

  const convWindow = day.convectionStart !== '--' && day.convectionEnd !== '--'
    ? `Convection active de ${day.convectionStart} à ${day.convectionEnd}`
    : '';
  const plafondStr = day.maxCumulusBase > 0 ? `Plafond à ${day.maxCumulusBase} m AGL (${mToFt(day.maxCumulusBase)} ft).` : '';
  const vzStr = day.maxVZ > 0 ? `VZ max ${day.maxVZ} m/s.` : '';
  const stormStr = day.stormRisk > 30 ? ` Risque orageux ${day.stormRisk}% — prudence.` : '';
  const bestStr = isBestDay ? ' Meilleure fenêtre de la semaine — à ne pas manquer.' : '';
  const windStr = day.windSurface.speed > 0 ? `Vent ${day.windSurface.dir} ${day.windSurface.speed} km/h.` : '';

  if (scenario === 'belle-journee') {
    return `${geoCtx}Excellente journée en perspective ${terrainDesc}. ${convWindow}. ${plafondStr} ${vzStr} ${sky}. ${windStr}${stormStr}${bestStr}${regional}`;
  }
  if (scenario === 'post-frontal') {
    return `${geoCtx}La masse d'air semble se renouveler ${terrainDesc} - conditions en nette amelioration. ${convWindow}. ${plafondStr} ${vzStr} ${sky}.${stormStr}${bestStr}${regional}`;
  }
  if (scenario === 'front-arrivant') {
    return `${geoCtx}Journee ${verdictLabel(day.verdict)} ${terrainDesc} mais a surveiller : signal de degradation possible. ${convWindow ? convWindow + '.' : ''} ${plafondStr} A confirmer avec la meteo officielle. ${windStr}${stormStr}${regional}`;
  }
  if (scenario === 'mediocre') {
    return `${geoCtx}Journée médiocre ${terrainDesc}. ${day.verdict !== 'unflyable' && convWindow ? convWindow + ' — activité thermique limitée.' : 'Activité thermique limitée.'} ${plafondStr} ${sky}.${stormStr}${regional}`;
  }
  // neutre
  return `${geoCtx}Journée ${verdictLabel(day.verdict)} ${terrainDesc}. ${convWindow ? convWindow + '.' : ''} ${plafondStr} ${vzStr} ${sky}. ${windStr}${stormStr}${bestStr}${regional}`;
}

/** Find the best day index in the week (0 = today) — only days with score >= 15 qualify */
function findBestDayIndex(days: DailySoaringForecast[]): number {
  let bestIdx = 0;
  let bestScore = -1;
  days.forEach((d, i) => {
    if (d.qualityScore >= 15 && d.qualityScore > bestScore) {
      bestScore = d.qualityScore;
      bestIdx = i;
    }
  });
  return bestIdx;
}

/** Determine the cause of an unflyable / hangar day */
function hangarCause(day: DailySoaringForecast): string {
  if (day.maxVZ >= 1.3 && day.maxCumulusBase >= 700 && day.stormRisk < 60) return 'Vol local possible malgre la couverture nuageuse';
  if ((day.tTdSpread ?? 10) < 2) return 'Risque d\'étalement immédiat - Ciel soudé';
  if (day.stormRisk > 60) return 'Risque orageux majeur';
  if ((day.windGust ?? 0) > 55) return 'Rafales dangereuses';
  if (day.windSurface.speed > 40) return 'Vent violent';
  if ((day.windGust ?? 0) > 40) return 'Rafales dangereuses';
  if (day.maxCumulusBase <= 0 || day.maxCumulusBase < 100) return 'Plafond au sol';
  if (day.verdict === 'unflyable') return 'Pluie ou couverture nuageuse totale';
  return 'Masse d\'air trop stable';
}

/** Classify aerodrome geographic zone */
type GeoZone = 'saguenay' | 'north-quebec' | 'south-quebec' | 'maritimes' | 'ontario' | 'vinon' | 'provence' | 'other';

function classifyGeoZone(aerodrome: AerodromeInfo): GeoZone {
  const { icao, lat, lon } = aerodrome;
  // Vinon-sur-Verdon (LFNF) — Mistral / valley wind territory
  if (icao === 'LFNF') return 'vinon';
  // Provence / South France (Mistral zone)
  if (lat >= 43.0 && lat <= 44.5 && lon >= 4.0 && lon <= 7.5) return 'provence';
  // Saguenay / Lac-Saint-Jean region
  if (icao === 'CYRC' || icao === 'CYRJ' || icao === 'CYMT' || (lat >= 48.0 && lat <= 50.5 && lon >= -75 && lon <= -69)) return 'saguenay';
  // Northern Quebec (above 50°N)
  if (lat > 50) return 'north-quebec';
  // Southern Quebec (below 47°N) — Montérégie, Estrie, Laurentides sud
  if (lat < 47 && lon > -76 && lon < -70) return 'south-quebec';
  // Maritimes
  if (lon > -67) return 'maritimes';
  // Ontario influence
  if (lon < -76) return 'ontario';
  return 'other';
}

/** Get local terrain descriptor for the aerodrome */
function getTerrainDescriptor(aerodrome: AerodromeInfo): string {
  const zone = classifyGeoZone(aerodrome);
  switch (zone) {
    case 'vinon': return 'à Vinon';
    case 'provence': return 'en Provence';
    case 'saguenay': return 'au Saguenay';
    case 'north-quebec': return 'en région nordique';
    case 'south-quebec': return 'dans le sud de la province';
    case 'maritimes': return 'en région maritime';
    case 'ontario': return 'en bordure ontarienne';
    default: return `sur ${aerodrome.name}`;
  }
}

/** Detect "why thermals fail" scenario: VZ < 1.0 m/s and wind > 30 km/h */
function thermalFailNarrative(forecast: DailySoaringForecast, aerodrome: AerodromeInfo): string | null {
  const vz = forecast.maxVZ ?? 0;
  const wind = forecast.windSurface.speed;
  if (vz < 1.0 && wind > 30) {
    const zone = classifyGeoZone(aerodrome);
    if (zone === 'vinon') {
      return `Le Mistral déchire les thermiques avant qu'ils ne se forment. Distance exploitable : 0 km.`;
    }
    if (zone === 'saguenay') {
      return `Le vent du fjord déchire les thermiques avant qu'ils ne se forment. Distance exploitable : 0 km.`;
    }
    return `Le vent déchire les thermiques avant qu'ils ne se forment. Distance exploitable : 0 km.`;
  }
  return null;
}

/** Generate geographic context sentence for the narrative */
function geoContextSentence(
  aerodrome: AerodromeInfo,
  forecast: DailySoaringForecast,
  weekDays: DailySoaringForecast[]
): string {
  const zone = classifyGeoZone(aerodrome);
  const windDir = forecast.windSurface.dir.toUpperCase();
  const windSpeed = forecast.windSurface.speed;
  const qnh = deriveQNH(forecast);
  const dewDay = deriveDewPoint(forecast);
  const spread = forecast.tempMax - dewDay;

  // Compute south-Quebec representative score (average of south-zone proxy)
  // We use the week's best score as a proxy for "south conditions"
  const weekBest = weekDays.length > 0 ? Math.max(...weekDays.map((d) => d.qualityScore)) : forecast.qualityScore;

  if (zone === 'vinon') {
    // ── v1.1.0: Mistral fort de secteur Nord/Nord-Ouest → message d'alerte ──
    const isNorthOrNW = windDir === 'N' || windDir === 'NNO' || windDir === 'NO' || windDir === 'NNE';
    if (isNorthOrNW && windSpeed > 30) {
      return `Mistral fort. Conditions turbulentes et dangereuses en planeur. Restez au sol. `;
    }
    // Mistral / valley wind references for Vinon-sur-Verdon
    if (windDir.includes('N') && windSpeed > 20) {
      return `Le Mistral souffle fort (${windSpeed} km/h) — les thermiques seront déchirés avant de se former. `;
    }
    if (windDir.includes('N') && windSpeed > 10) {
      return `Vent de vallée du Nord (${windSpeed} km/h) — influence du Mistral modéré sur la Durance. `;
    }
    if (windDir.includes('S') && windSpeed > 15) {
      return `Flux de Sud remontant la vallée de la Durance (${windSpeed} km/h) — humidité en hausse depuis la Méditerranée. `;
    }
    if (forecast.qualityScore >= 60) {
      return `La topographie de Vinon favorise une bonne organisation des thermiques de pente sur le Verdon. `;
    }
    return `Conditions locales à Vinon influencées par le relief de la Durance et le régime du Mistral. `;
  }

  if (zone === 'provence') {
    if (windDir.includes('N') && windSpeed > 25) {
      return `Le Mistral s'impose (${windSpeed} km/h) — thermiques impossibles à centrer dans ce flux. `;
    }
    if (windDir.includes('N') && windSpeed > 10) {
      return `Vent de Nord modéré (${windSpeed} km/h) — influence du Mistral, masse d'air sèche et fraîche. `;
    }
    return `Conditions provençales — surveiller le régime du Mistral et les vents de vallée. `;
  }

  if (zone === 'saguenay') {
    // ── v1.2.0: Only truly critical T/Td spread (<2°C) triggers étalement message ──
    if (spread < 2) {
      return `L'humidité élevée liée au fjord favorise un étalement immédiat dès les premiers thermiques — le ciel se couvrira rapidement. `;
    }
    if (spread < 4) {
      return `L'humidité du fjord est présente (écart T/Td ${spread.toFixed(0)}°C) — risque d'étalement en phase convective. `;
    }
    // Fjord / valley wind influence
    if (windDir.includes('N') && windSpeed > 15) {
      return `Les vents de vallée du fjord s'engouffrent par le Nord (${windSpeed} km/h), comprimant la masse d'air en basse couche. `;
    }
    if (windDir.includes('S') && windSpeed > 10) {
      return `Le flux de Sud remonte le fjord (${windSpeed} km/h), apportant une humidité supplémentaire depuis le Saint-Laurent. `;
    }
    if (qnh < 1008) {
      return `L'humidite du fjord peut maintenir une couche basse plus humide; surveiller l'etalement local. `;
    }
    if (forecast.qualityScore >= 60) {
      return `La topographie du Saguenay favorise aujourd'hui une bonne organisation des thermiques de pente. `;
    }
    return `Les conditions locales du Saguenay sont influencées par le relief du fjord. `;
  }

  if (zone === 'south-quebec') {
    if (windDir.includes('S') || windDir.includes('SW') || windDir.includes('SO')) {
      return `Flux de Sud-Ouest (${windSpeed} km/h) - masse d'air possiblement plus humide; surveiller l'evolution locale. `;
    }
    if (forecast.qualityScore >= 65) {
      return `Conditions locales favorables dans le sud du Quebec selon les parametres disponibles. `;
    }
    return `Conditions locales du sud du Quebec evaluees a partir des donnees disponibles. `;
  }

  return '';
}

/** Generate regional comparison sentence (only when supported by app data) */
function regionalComparisonSentence(
  aerodrome: AerodromeInfo,
  forecast: DailySoaringForecast,
  weekDays: DailySoaringForecast[]
): string {
  return '';
}

/** Generate visual sky description based on conditions */
function skyDescription(forecast: DailySoaringForecast): string {
  const dewDay = deriveDewPoint(forecast);
  const spread = forecast.tempMax - dewDay;
  const thermal = thermalType(forecast);
  const qnh = deriveQNH(forecast);

  if (forecast.verdict === 'unflyable' || forecast.qualityScore < 15) {
    if (forecast.stormRisk > 50) return 'ciel menaçant avec cumulo-nimbus en développement';
    return 'ciel couvert et plafond au sol';
  }
  if (spread >= 10 && thermal === 'bleu') return 'ciel bleu limpide, thermiques secs sans marquage nuageux';
  if (spread >= 7 && thermal === 'bleu') return 'ciel bleu avec quelques cumulus épars en altitude';
  if (spread >= 6 && thermal === 'cumulus') return 'cumulus bien dessinés à base nette, bien organisés';
  if (spread >= 4 && thermal === 'cumulus') return 'cumulus bien formés, base estimée à ' + forecast.maxCumulusBase + ' m AGL';
  if (spread < 4 && forecast.stormRisk > 30) return 'ciel laiteux avec cumulus bourgeonnants — risque d\'étalement';
  if (spread < 4) return 'ciel laiteux et thermiques anémiques sous une couche humide';
  if (qnh < 1008) return 'ciel voilé de stratus bas, visibilité réduite';
  if (forecast.highCloudFilter) return 'voile de cirrus filtrant le rayonnement solaire';
  return 'ciel partiellement nuageux, conditions mixtes';
}

/** Generate precise trigger time with visual context */
function triggerTimeDescription(forecast: DailySoaringForecast, aerodrome: AerodromeInfo): string {
  const zone = classifyGeoZone(aerodrome);
  const convStart = forecast.convectionStart !== '--' ? forecast.convectionStart : null;
  if (!convStart) return '';

  const sky = skyDescription(forecast);
  const dewDay = deriveDewPoint(forecast);
  const spread = forecast.tempMax - dewDay;

  // Add geographic flavor to trigger time
  if (zone === 'saguenay') {
    return `Les premiers thermiques déclencheront vers ${convStart} sur les versants exposés du Saguenay — attendez-vous à un ${sky}.`;
  }
  if (zone === 'south-quebec') {
    return `Déclenchement thermique prévu vers ${convStart} — ${sky}.`;
  }
  return `Déclenchement thermique estimé à ${convStart} — ${sky}.`;
}

/** Humanized hangar message with chef pilote voice */
function humanizedHangarMessage(forecast: DailySoaringForecast, aerodrome: AerodromeInfo): string {
  const zone = classifyGeoZone(aerodrome);
  const cause = hangarCause(forecast);
  const terrainDesc = getTerrainDescriptor(aerodrome);
  const geoCtx = geoContextSentence(aerodrome, forecast, []);
  const regional = regionalComparisonSentence(aerodrome, forecast, []);
  const wind = forecast.windSurface.speed;
  const gust = forecast.windGust ?? 0;
  const vz = forecast.maxVZ ?? 0;
  const windDir = forecast.windSurface.dir.toUpperCase();
  const dewDay = deriveDewPoint(forecast);
  const spread = forecast.tempMax - dewDay;
  const localSoaringPossible = forecast.maxVZ >= 1.3 && forecast.maxCumulusBase >= 700 && forecast.stormRisk < 60;

  // "Why thermals fail" narrative
  const failNarrative = thermalFailNarrative(forecast, aerodrome);

  if (zone === 'vinon') {
    // ── v1.1.0: Mistral fort de secteur Nord/Nord-Ouest ──
    const isNorthOrNW = windDir === 'N' || windDir === 'NNO' || windDir === 'NO' || windDir === 'NNE';
    if (isNorthOrNW && wind > 30) {
      return `Mistral fort. Conditions turbulentes et dangereuses en planeur. Restez au sol.${regional}`;
    }
    if (wind > 25 || gust > 40) {
      return `Trop de vent pour spiraler en sécurité ${terrainDesc}. ${geoCtx}${failNarrative ?? `Le Mistral rend le vol impossible aujourd'hui.`} Restez au hangar.${regional}`;
    }
    if (vz < 0.5 || (forecast.maxCumulusBase < 200 && forecast.maxVZ < 0.5)) {
      return `Moteur thermique éteint ${terrainDesc}. ${geoCtx}Masse d'air trop stable, aucune convection exploitable. Journée à consacrer à l'entretien du matériel.${regional}`;
    }
    if (forecast.stormRisk > 60) {
      return `Journée non propice ${terrainDesc} — les orages rendent le vol impossible. ${geoCtx}Inutile de sortir les machines, allez plutôt prendre un café et regarder les cumulo-nimbus de loin.${regional}`;
    }
    return `Journée non propice ${terrainDesc}. ${geoCtx}[Cause : ${cause}]. Journée à consacrer à l'entretien du matériel ou à la théorie.${regional}`;
  }

  if (zone === 'saguenay') {
    if (localSoaringPossible) {
      return `Aujourd'hui ${terrainDesc}, le ciel peut rester tres couvert, mais le profil reste exploitable localement: VZ ${forecast.maxVZ} m/s et plafond ${forecast.maxCumulusBase} m AGL. Vol local possible avec prudence, en gardant une marge confortable sous la base.${regional}`;
    }
    // ── v1.2.0: Only truly critical T/Td spread (<2°C) triggers étalement message ──
    if (spread < 2) {
      return `Aujourd'hui ${terrainDesc}, l'humidité élevée liée au fjord provoque un étalement immédiat dès les premiers thermiques. Le ciel se couvrira rapidement — restez au sol.${regional}`;
    }
    if (wind > 25 || gust > 40) {
      return `Trop de vent pour spiraler en sécurité ${terrainDesc}. ${geoCtx}${failNarrative ?? `Le vent du fjord rend le vol dangereux.`} Restez au hangar.${regional}`;
    }
    if (vz < 0.5) {
      return `Moteur thermique éteint ${terrainDesc}. ${geoCtx}L'humidité du fjord sature la masse d'air. Inutile de sortir les machines, allez plutôt prendre un café.${regional}`;
    }
    if (forecast.stormRisk > 60) {
      return `Aujourd'hui ${terrainDesc}, on reste au sol — les orages rendent le vol impossible. ${geoCtx}Inutile de sortir les machines, allez plutôt prendre un café et regarder les cumulo-nimbus de loin.${regional}`;
    }
    if (cause === 'Plafond au sol' || cause === 'Pluie ou couverture nuageuse totale') {
      return `Aujourd'hui ${terrainDesc}, on reste au sol. Le plafond est trop bas et l'humidité du fjord sature la masse d'air. ${geoCtx}Inutile de sortir les machines, allez plutôt prendre un café.${regional}`;
    }
    return `Aujourd'hui ${terrainDesc}, les conditions sont incompatibles avec le vol à voile. [Cause : ${cause}]. ${geoCtx}Journée à consacrer à l'entretien du matériel ou à la théorie.${regional}`;
  }

  if (zone === 'north-quebec') {
    if (wind > 25 || gust > 40) {
      return `Trop de vent pour spiraler en sécurité ${terrainDesc}. ${geoCtx}${failNarrative ?? `Le vent du Nord rend le vol impossible aujourd'hui.`} Restez au hangar.${regional}`;
    }
    if (vz < 0.5) {
      return `Moteur thermique éteint ${terrainDesc}. ${geoCtx}L'humidité du fjord sature la masse d'air. Inutile de sortir les machines, allez plutôt prendre un café.${regional}`;
    }
    if (forecast.stormRisk > 60) {
      return `Journée non propice ${terrainDesc} — les orages rendent le vol impossible. ${geoCtx}Inutile de sortir les machines, allez plutôt prendre un café et regarder les cumulo-nimbus de loin.${regional}`;
    }
    if (cause === 'Plafond au sol' || cause === 'Pluie ou couverture nuageuse totale') {
      return `Aujourd'hui ${terrainDesc}, on reste au sol. Le plafond est trop bas et l'humidité du fjord sature la masse d'air. ${geoCtx}Inutile de sortir les machines, allez plutôt prendre un café.${regional}`;
    }
    return `Aujourd'hui ${terrainDesc}, les conditions sont incompatibles avec le vol à voile. [Cause : ${cause}]. ${geoCtx}Journée à consacrer à l'entretien du matériel ou à la théorie.${regional}`;
  }

  if (cause === 'Vent violent' || cause === 'Rafales dangereuses') {
    return `Trop de vent pour spiraler en sécurité ${terrainDesc}. ${failNarrative ?? `Conditions incompatibles avec le vol à voile. [Cause : ${cause}].`} Restez au hangar, c'est une décision sage.${regional}`;
  }
  if (cause === 'Risque orageux majeur') {
    return `Journée non propice ${terrainDesc}. Conditions incompatibles avec le vol à voile. [Cause : ${cause}]. Allez prendre un café — les orages ne pardonnent pas.${regional}`;
  }
  if (cause === 'Masse d\'air trop stable') {
    return `Moteur thermique éteint ${terrainDesc}. Masse d'air trop stable, aucune convection exploitable. Journée à consacrer à l'entretien du matériel ou à la théorie.${regional}`;
  }
  return `Journée non propice ${terrainDesc}. Conditions incompatibles avec le vol à voile. [Cause : ${cause}]. Journée à consacrer à l'entretien du matériel ou à la théorie.${regional}`;
}

/** Generate day narrative — legacy wrapper kept for compatibility */
function generateDayNarrative(
  day: DailySoaringForecast,
  prevDay: DailySoaringForecast,
  qnh: number
): string {
  return generateExpertNarrative(day, prevDay, null, false, []);
}

function skyTimelineSummary(day: DailySoaringForecast): string | null {
  const hours = (day.hourly ?? []).filter((h) => h.time >= 8 && h.time <= 18);
  if (hours.length === 0) return null;

  const morning = hours.filter((h) => h.time >= 8 && h.time <= 10);
  const midday = hours.filter((h) => h.time >= 11 && h.time <= 14);
  const evening = hours.filter((h) => h.time >= 16 && h.time <= 18);
  const avg = (values: number[]) => values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
  const morningCloud = avg(morning.map((h) => h.cloudCover ?? 0));
  const middayCu = avg(midday.map((h) => h.cloudCoverLow ?? 0));
  const middayMid = avg(midday.map((h) => h.cloudCoverMid ?? 0));
  const middayHigh = avg(midday.map((h) => h.cloudCoverHigh ?? 0));
  const eveningCu = avg(evening.map((h) => h.cloudCoverLow ?? 0));
  const eveningHigh = avg(evening.map((h) => h.cloudCoverHigh ?? 0));
  const bestCuHour = hours
    .filter((h) => (h.cloudCoverLow ?? 0) >= 20 || h.skyType === 'cumulus')
    .sort((a, b) => (b.vz ?? 0) - (a.vz ?? 0))[0];

  const parts: string[] = [];
  if (morningCloud >= 65) parts.push('Matinee souvent couverte entre 8h et 10h');
  else if (morningCloud >= 40) parts.push('Matinee encore partagee entre nuages et eclaircies');
  else parts.push('Matinee deja assez ouverte');

  if (middayCu >= 25 && middayMid < 45 && middayHigh < 45) {
    parts.push(`degagement progressif avec cumulus possibles vers ${bestCuHour ? `${bestCuHour.time}h` : '13h-14h'}`);
  } else if (middayHigh >= 45 || middayMid >= 45) {
    parts.push('fenetre de mi-journee genee par un voile ou des nuages moyens');
  } else {
    parts.push('mi-journee plutot bleue ou peu marquee');
  }

  if (eveningCu < 25 && eveningHigh < 35) parts.push('fin de journee plus bleue');
  else if ((day.limitingFactors ?? []).some((f) => f.includes('fin de journee'))) parts.push('surveiller etalement ou cunimb en fin de journee');
  else parts.push('nuages encore presents en fin de journee');

  return `${parts.join(', ')}.`;
}

function weeklyTrendDetails(day: DailySoaringForecast): string | null {
  const hours = (day.hourly ?? []).filter((h) => h.time >= 8 && h.time <= 18);
  if (hours.length === 0) return null;

  const bestCuHour = hours
    .filter((h) => (h.cloudCoverLow ?? 0) >= 18 || h.skyType === 'cumulus')
    .sort((a, b) => ((b.cloudCoverLow ?? 0) + (b.vz ?? 0) * 12) - ((a.cloudCoverLow ?? 0) + (a.vz ?? 0) * 12))[0];
  const midCloudHour = hours
    .filter((h) => (h.cloudCoverMid ?? 0) >= 25)
    .sort((a, b) => (b.cloudCoverMid ?? 0) - (a.cloudCoverMid ?? 0))[0];
  const highCloudHour = hours
    .filter((h) => (h.cloudCoverHigh ?? 0) >= 25)
    .sort((a, b) => (b.cloudCoverHigh ?? 0) - (a.cloudCoverHigh ?? 0))[0];
  const blueReturnHour = hours.find((h) =>
    h.time >= 15 &&
    (h.cloudCoverLow ?? 0) < 25 &&
    (h.cloudCoverMid ?? 0) < 35 &&
    (h.cloudCoverHigh ?? 0) < 35
  );
  const lateRiskHour = hours.find((h) =>
    h.time >= 16 &&
    (
      h.pilotSkySignal === 'Developpement vertical rapide' ||
      (h.precipProbability ?? 0) >= 45 ||
      (h.cloudCoverLow ?? 0) >= 75
    )
  );

  const parts: string[] = [];
  if (bestCuHour) {
    const octasValue = Math.max(0, Math.min(8, Math.round((bestCuHour.cloudCoverLow ?? 0) / 12.5)));
    const octas = octasValue > 0 ? `, ${octasValue}/8` : '';
    parts.push(`Cu probables vers ${bestCuHour.hour}${octas}`);
  } else if (day.maxVZ >= 1) {
    parts.push('thermiques plutot bleus');
  }

  if (midCloudHour) parts.push(`nuages moyens vers ${midCloudHour.hour} (${Math.round(midCloudHour.cloudCoverMid ?? 0)}%)`);
  if (highCloudHour) parts.push(`voile/cirrus vers ${highCloudHour.hour} (${Math.round(highCloudHour.cloudCoverHigh ?? 0)}%)`);
  if (blueReturnHour) parts.push(`retour plus bleu apres ${blueReturnHour.hour}`);
  if (lateRiskHour || (day.limitingFactors ?? []).some((f) => f.includes('fin de journee'))) {
    parts.push('risque a surveiller en fin de journee');
  }

  return parts.length > 0 ? parts.join(' - ') : null;
}

function DailyBriefingContent() {
  const { selectedAerodrome } = useAerodrome();
  const [today, setToday] = useState<DailySoaringForecast>(MOCK_TODAY_FORECAST);
  const [week, setWeek] = useState<DailySoaringForecast[]>(MOCK_WEEK_FORECAST);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastIcaoRef = useRef<string>('');
  const printRef = useRef<HTMLDivElement>(null);

  const loadForecast = useCallback(async (aerodrome: AerodromeInfo) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSoaringForecast(aerodrome);
      setToday(result.today);
      setWeek(result.week);
    } catch {
      setError('Impossible de charger les données météo. Données de démonstration affichées.');
      setToday(MOCK_TODAY_FORECAST);
      setWeek(MOCK_WEEK_FORECAST);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedAerodrome.icao !== lastIcaoRef.current) {
      lastIcaoRef.current = selectedAerodrome.icao;
      loadForecast(selectedAerodrome);
    }
  }, [selectedAerodrome, loadForecast]);

  // Derived data
  const [dateLabel, setDateLabel] = useState('');
  useEffect(() => {
    setDateLabel(new Date().toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }));
  }, []);

  const elevM = selectedAerodrome.elevation ?? 0;
  const elevFt = mToFt(elevM);

  // Find hour of min/max temperature in today's hourly data
  const hourlyData = today.hourly ?? [];
  const minTempHour = hourlyData.reduce((best, h) => h.temp < best.temp ? h : best, hourlyData[0] ?? { temp: today.tempMin, hour: '--:--' });
  const maxTempHour = hourlyData.reduce((best, h) => h.temp > best.temp ? h : best, hourlyData[0] ?? { temp: today.tempMax, hour: '--:--' });
  const maxCeilingHour = hourlyData.reduce((best, h) => (h.cumulusBase ?? 0) > (best.cumulusBase ?? 0) ? h : best, hourlyData[0] ?? { cumulusBase: today.maxCumulusBase, hour: '--:--' });
  const maxVzHour = hourlyData.reduce((best, h) => (h.vz ?? 0) > (best.vz ?? 0) ? h : best, hourlyData[0] ?? { vz: today.maxVZ, hour: '--:--' });
  const maxGustHour = hourlyData.reduce((best, h) => (h.windSpeed ?? 0) > (best.windSpeed ?? 0) ? h : best, hourlyData[0] ?? { windSpeed: today.windSurface.speed, hour: '--:--' });
  const bestCuHour = hourlyData
    .filter((h) => (h.cloudCoverLow ?? 0) >= 20 || h.skyType === 'cumulus')
    .sort((a, b) => ((b.cloudCoverLow ?? 0) + (b.vz ?? 0) * 12) - ((a.cloudCoverLow ?? 0) + (a.vz ?? 0) * 12))[0];
  const midCloudPeakHour = hourlyData
    .filter((h) => (h.cloudCoverMid ?? 0) >= 20)
    .sort((a, b) => (b.cloudCoverMid ?? 0) - (a.cloudCoverMid ?? 0))[0];
  const highCloudPeakHour = hourlyData
    .filter((h) => (h.cloudCoverHigh ?? 0) >= 20)
    .sort((a, b) => (b.cloudCoverHigh ?? 0) - (a.cloudCoverHigh ?? 0))[0];
  const blueReturnHour = hourlyData.find((h) => h.time >= 15 && (h.cloudCoverLow ?? 100) < 25 && (h.cloudCoverMid ?? 100) < 35 && (h.cloudCoverHigh ?? 100) < 35);
  const lateRiskHour = hourlyData.find((h) => h.time > 16 && (h.pilotSkySignal === 'Developpement vertical rapide' || (h.precipProbability ?? 0) >= 45 || (h.cloudCoverLow ?? 0) >= 75));

  // First convective hour (thermal trigger)
  const firstConvHour = hourlyData.find((h) => h.isConvective);
  const triggerHour = firstConvHour?.hour ?? today.convectionStart ?? '--:--';
  const triggerTemp = today.triggerTemp ?? firstConvHour?.temp ?? '--';

  // Next 2-3 days for trends
  const nextDays = week.slice(1, 4);

  // Aeronautical derived values
  const qnh = deriveQNH(today);
  const visibility = deriveVisibility(today);
  const dewpoint = deriveDewPoint(today);
  const spread = today.tempMax - dewpoint;

  // Causal analyses
  const pressureText = pressureAnalysis(qnh, today);
  const humidityText = humidityAnalysis(dewpoint, today.tempMax);
  const windText = windAnalysis(today);
  const rainText = rainAnalysis(today);
  const rainSummary = rainSummarySentence(today);
  const todayRainProb = rainProbability(today);
  const todayRainMm = rainTotalMm(today);
  const frontText = frontPassageAnalysis(today, nextDays);

  // Geographic intelligence for today
  const todayGeoCtx = geoContextSentence(selectedAerodrome, today, nextDays);
  const todayRegional = regionalComparisonSentence(selectedAerodrome, today, nextDays);
  const todaySky = skyDescription(today);
  const todayTrigger = triggerTimeDescription(today, selectedAerodrome);
  const todayTerrainDesc = getTerrainDescriptor(selectedAerodrome);
  const todayTimeline = skyTimelineSummary(today);
  const dailySummaryText = cleanBriefingText(
    today.qualityScore < 15
      ? humanizedHangarMessage(today, selectedAerodrome)
      : todayTimeline
        ? `${todayTimeline} ${today.convectionStart !== '--' ? `Fenetre thermique de ${today.convectionStart} a ${today.convectionEnd}.` : ''} Plafond maximum estime a ${today.maxCumulusBase} m AGL.${rainSummary}${today.stormRisk > 40 ? ` Risque orageux significatif (${today.stormRisk}%) - prudence accrue requise.` : ''}${todayRegional}`
      : today.qualityScore >= 65 && today.verdict === 'favorable'
        ? `${todayGeoCtx}Enfin une fenêtre exploitable ${todayTerrainDesc} ! ${todayTrigger || `Convection active de ${today.convectionStart} à ${today.convectionEnd} — ${todaySky}`}. Plafond maximum à ${today.maxCumulusBase} m AGL.${rainSummary}${today.stormRisk > 40 ? ` Risque orageux significatif (${today.stormRisk}%) - prudence accrue requise.` : ''}${todayRegional}`
        : `${todayGeoCtx}Journée ${verdictLabel(today.verdict)} ${todayTerrainDesc}. ${today.verdict !== 'unflyable' && today.convectionStart !== '--' ? `${todayTrigger || `Convection active de ${today.convectionStart} à ${today.convectionEnd} — ${todaySky}`}. Plafond maximum estimé à ${today.maxCumulusBase} m AGL.` : 'Conditions non favorables au vol thermique aujourd\'hui.'}${rainSummary}${today.stormRisk > 40 ? ` Risque orageux significatif (${today.stormRisk}%) - prudence accrue requise.` : ''}${todayRegional}`
  );

  // Overall verdict color
  const verdictColor = today.verdict === 'favorable' ? '#34d399'
    : today.verdict === 'marginal' ? '#fbbf24'
    : today.verdict === 'storm-risk'? '#f97316' :'#f87171';

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #briefing-print-area, #briefing-print-area * { visibility: visible !important; }
          #briefing-print-area { position: absolute; left: 0; top: 0; width: 100%; background: white !important; color: black !important; }
          #briefing-print-area .no-print { display: none !important; }
          .print-card { background: #f8fafc !important; border: 1px solid #cbd5e1 !important; color: #1e293b !important; page-break-inside: avoid; }
          .print-text { color: #1e293b !important; }
          .print-muted { color: #64748b !important; }
          .print-accent { color: #0369a1 !important; }
        }
      `}</style>

      <div className="min-h-screen px-3 sm:px-4 md:px-6 xl:px-8 2xl:px-10 py-4 sm:py-6 max-w-screen-2xl mx-auto" id="briefing-print-area" ref={printRef}>

        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 no-print">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl flex-shrink-0" style={{ backgroundColor: 'rgba(126,200,227,0.1)', border: '1px solid rgba(126,200,227,0.25)' }}>
              <FileText size={22} style={{ color: '#7ec8e3' }} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold print-text" style={{ color: 'var(--text-primary)' }}>Briefing Quotidien</h1>
              <p className="text-xs sm:text-sm mt-0.5 capitalize print-muted" style={{ color: 'var(--text-muted)' }}>{dateLabel}</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-150 hover:opacity-90 touch-manipulation self-start sm:self-auto"
            style={{ backgroundColor: 'rgba(126,200,227,0.15)', border: '1px solid rgba(126,200,227,0.35)', color: '#7ec8e3' }}
          >
            <Printer size={16} />
            Générer le PDF de briefing
          </button>
        </div>

        {/* Print header (visible in print only) */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Briefing Quotidien — SoarCast</h1>
          <p className="text-sm capitalize">{dateLabel}</p>
        </div>

        {/* ── Loading / Error ── */}
        {loading && (
          <div className="rounded-xl p-4 mb-4 flex items-center gap-3" style={{ backgroundColor: 'rgba(126,200,227,0.06)', border: '1px solid rgba(126,200,227,0.15)' }}>
            <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#7ec8e3', borderTopColor: 'transparent' }} />
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Chargement des données pour {selectedAerodrome.icao}…</span>
          </div>
        )}
        {error && (
          <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)' }}>
            <p className="text-sm" style={{ color: '#fbbf24' }}>{error}</p>
          </div>
        )}

        {/* ── SECTION 1: Terrain Identity Header ── */}
        <div className="rounded-xl p-4 sm:p-5 mb-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
          <div className="flex items-start gap-3 mb-4">
            <MapPin size={18} style={{ color: '#7ec8e3', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-lg font-bold print-text" style={{ color: 'var(--text-primary)' }}>
                {selectedAerodrome.name} — <span style={{ color: '#7ec8e3' }}>{selectedAerodrome.icao}</span>
              </h2>
              <p className="text-xs sm:text-sm mt-1 print-muted" style={{ color: 'var(--text-muted)' }}>
                Altitude terrain : <span className="font-semibold print-text" style={{ color: 'var(--text-secondary)' }}>{elevFt} ft / {elevM} m</span>
                &nbsp;·&nbsp;
                {selectedAerodrome.lat.toFixed(3)}°N, {Math.abs(selectedAerodrome.lon).toFixed(3)}°{selectedAerodrome.lon >= 0 ? 'E' : 'O'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Sunrise size={13} style={{ color: '#fbbf24' }} />
                <span className="text-xs font-medium uppercase tracking-wider" style={{ color: '#fbbf24' }}>Lever du soleil</span>
              </div>
              <p className="text-xl font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{today.sunrise}</p>
              <p className="text-xs print-muted" style={{ color: 'var(--text-muted)' }}>Heure locale</p>
            </div>
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Sunset size={13} style={{ color: '#f97316' }} />
                <span className="text-xs font-medium uppercase tracking-wider" style={{ color: '#f97316' }}>Coucher du soleil</span>
              </div>
              <p className="text-xl font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{today.sunset}</p>
              <p className="text-xs print-muted" style={{ color: 'var(--text-muted)' }}>Heure locale</p>
            </div>
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(126,200,227,0.06)', border: '1px solid rgba(126,200,227,0.15)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Zap size={13} style={{ color: '#7ec8e3' }} />
                <span className="text-xs font-medium uppercase tracking-wider" style={{ color: '#7ec8e3' }}>Convection</span>
              </div>
              <p className="text-sm font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{today.convectionStart} → {today.convectionEnd}</p>
              <p className="text-xs print-muted" style={{ color: 'var(--text-muted)' }}>Fenêtre active</p>
            </div>
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: `rgba(${today.verdict === 'favorable' ? '52,211,153' : today.verdict === 'marginal' ? '251,191,36' : '248,113,113'},0.08)`, border: `1px solid rgba(${today.verdict === 'favorable' ? '52,211,153' : today.verdict === 'marginal' ? '251,191,36' : '248,113,113'},0.25)` }}>
              <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: verdictColor }}>Verdict</p>
              <p className="text-sm font-bold capitalize print-text" style={{ color: verdictColor }}>{verdictLabel(today.verdict)}</p>
              <p className="text-xs print-muted" style={{ color: 'var(--text-muted)' }}>Score : {today.qualityScore}/100</p>
            </div>
          </div>
        </div>

        {/* ── SECTION 2: Summary Banner ── */}
        <div className="rounded-xl p-4 sm:p-5 mb-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>RÉSUMÉ DU JOUR</p>
          <p className="text-sm leading-relaxed print-text" style={{ color: 'var(--text-secondary)' }}>
            {dailySummaryText}
          </p>
        </div>

        {/* ── SECTION 3: Aeronautical Details (QNH, Visibility, Dew Point) ── */}
        <div className="rounded-xl p-4 sm:p-5 mb-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>DONNÉES AÉRONAUTIQUES</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-4">
            {/* QNH */}
            <div className="rounded-lg px-4 py-3" style={{ backgroundColor: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Gauge size={14} style={{ color: '#a78bfa' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#a78bfa' }}>Indice pression</span>
              </div>
              <p className="text-2xl font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{qnh} <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>indice</span></p>
              <p className="text-xs mt-1" style={{ color: qnh < 1005 ? '#f87171' : qnh < 1010 ? '#fbbf24' : '#34d399' }}>
                {qnh < 1005 ? 'Bas indicatif' : qnh < 1010 ? 'En baisse' : qnh > 1020 ? 'Eleve indicatif' : 'Stable'}
              </p>
            </div>
            {/* Visibility */}
            <div className="rounded-lg px-4 py-3" style={{ backgroundColor: 'rgba(126,200,227,0.06)', border: '1px solid rgba(126,200,227,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Eye size={14} style={{ color: '#7ec8e3' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#7ec8e3' }}>Visibilité Estimée</span>
              </div>
              <p className="text-2xl font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{visibility} <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>km</span></p>
              <p className="text-xs mt-1" style={{ color: visibility < 10 ? '#f87171' : visibility < 20 ? '#fbbf24' : '#34d399' }}>
                {visibility < 10 ? 'Réduite — précautions' : visibility < 20 ? 'Modérée' : 'Bonne à excellente'}
              </p>
            </div>
            {/* Dew Point */}
            <div className="rounded-lg px-4 py-3" style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Droplets size={14} style={{ color: '#34d399' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#34d399' }}>Point de Rosée</span>
              </div>
              <p className="text-2xl font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{dewpoint}°<span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>C</span></p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Écart T/Td : <span style={{ color: spread < 4 ? '#f87171' : spread < 8 ? '#fbbf24' : '#34d399', fontWeight: 600 }}>{spread.toFixed(0)}°C</span>
              </p>
            </div>
            {/* Rain / Showers */}
            <div className="rounded-lg px-4 py-3" style={{ backgroundColor: 'rgba(126,200,227,0.06)', border: '1px solid rgba(126,200,227,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <CloudRain size={14} style={{ color: todayRainProb >= 35 || todayRainMm >= 1 ? '#fbbf24' : '#34d399' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: todayRainProb >= 35 || todayRainMm >= 1 ? '#fbbf24' : '#34d399' }}>Pluie / Averses</span>
              </div>
              <p className="text-2xl font-bold font-mono print-text" style={{ color: 'var(--text-primary)' }}>{todayRainProb}<span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>%</span></p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{todayRainMm.toFixed(1)} mm prevus</p>
            </div>
          </div>
          {/* Spread explanation */}
          <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid var(--card-border)' }}>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              <span className="font-semibold" style={{ color: 'var(--text-secondary)' }}>Écart T/Td :</span>{' '}
              {spreadLabel(today.tempMax, dewpoint)}
            </p>
          </div>
          <div className="rounded-lg px-3 py-2.5 mt-2" style={{ backgroundColor: 'rgba(126,200,227,0.04)', border: '1px solid rgba(126,200,227,0.12)' }}>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              <span className="font-semibold" style={{ color: 'var(--text-secondary)' }}>Pluie / averses :</span>{' '}
              {rainText}
            </p>
          </div>
        </div>

        {/* ── SECTION 4: Thermal & Temperature ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          {/* Temperatures */}
          <div className="rounded-xl p-3 sm:p-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Thermometer size={16} style={{ color: '#fbbf24' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#fbbf24' }}>Températures</h2>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#7ec8e3' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Température minimale de <strong>{minTempHour?.temp ?? today.tempMin}°C</strong> prévue à{' '}
                  <strong>{minTempHour?.hour ?? '--:--'}</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#f97316' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Température maximale de <strong>{maxTempHour?.temp ?? today.tempMax}°C</strong> prévue à{' '}
                  <strong>{maxTempHour?.hour ?? '--:--'}</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#fbbf24' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Température de déclenchement thermique : <strong>{today.triggerTemp}°C</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#fbbf24' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Le seuil de declenchement est attendu autour de <strong>{triggerHour}</strong>.
                </span>
              </li>
            </ul>
          </div>

          {/* Convective Chronology */}
          <div className="rounded-xl p-3 sm:p-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Zap size={16} style={{ color: '#a78bfa' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#a78bfa' }}>Chronologie Convective</h2>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#a78bfa' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Déclenchement thermique estimé à <strong>{triggerHour}</strong> avec une température au sol de{' '}
                  <strong>{typeof triggerTemp === 'number' ? `${triggerTemp}°C` : triggerTemp}</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#7ec8e3' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Plafond maximum : <strong>{today.maxCumulusBase} m AGL</strong> ({mToFt(today.maxCumulusBase)} ft).
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#34d399' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  VZ maximale estimée : <strong>{today.maxVZ} m/s</strong> — Force thermique : <strong>{today.maxThermalStrength}/6</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#7ec8e3' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Plafond max vers <strong>{maxCeilingHour?.hour ?? '--:--'}</strong>, VZ max vers <strong>{maxVzHour?.hour ?? '--:--'}</strong>.
                </span>
              </li>
              {bestCuHour && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#86efac' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                    Cumulus les plus probables autour de <strong>{bestCuHour.hour}</strong>
                    {typeof bestCuHour.cloudCoverLow === 'number' ? ` (${Math.round(bestCuHour.cloudCoverLow / 12.5)}/8 environ)` : ''}.
                  </span>
                </li>
              )}
              {blueReturnHour && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#7ec8e3' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                    Tendance plus bleue possible apres <strong>{blueReturnHour.hour}</strong>.
                  </span>
                </li>
              )}
              {today.overdevelopmentRisk && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#f97316' }} />
                  <span className="text-sm" style={{ color: '#fed7aa' }}>
                    Risque de surdéveloppement convectif en fin de journée.
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* ── SECTION 5: Wind & Clouds ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="rounded-xl p-3 sm:p-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Wind size={16} style={{ color: '#7ec8e3' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#7ec8e3' }}>Vent & Turbulences</h2>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#7ec8e3' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Vent de surface : <strong>{today.windSurface.speed} km/h secteur {today.windSurface.dir}</strong>
                  {today.windGust > today.windSurface.speed + 5 ? `, rafales à ${today.windGust} km/h` : ''}.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#b0d4e8' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Vent en altitude (2 000 m) : <strong>{today.windAloft2000.speed} km/h secteur {today.windAloft2000.dir}</strong>.
                </span>
              </li>
              {today.windShearFragmented && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#f87171' }} />
                  <span className="text-sm" style={{ color: '#fca5a5' }}>Cisaillement de vent significatif — thermiques fragmentés possibles.</span>
                </li>
              )}
              {today.waveConditions && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#a78bfa' }} />
                  <span className="text-sm" style={{ color: '#c4b5fd' }}>Conditions de vol en onde favorables.</span>
                </li>
              )}
            </ul>
          </div>

          <div className="rounded-xl p-3 sm:p-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Cloud size={16} style={{ color: '#a78bfa' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#a78bfa' }}>Nébulosité & Plafond</h2>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#a78bfa' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Base des cumulus maximale : <strong>{today.maxCumulusBase} m AGL</strong> ({mToFt(today.maxCumulusBase)} ft).
                </span>
              </li>
              {midCloudPeakHour && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#fbbf24' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                    Nuages moyens possibles vers <strong>{midCloudPeakHour.hour}</strong>
                    {typeof midCloudPeakHour.cloudCoverMid === 'number' ? ` (${midCloudPeakHour.cloudCoverMid}%)` : ''}.
                  </span>
                </li>
              )}
              {highCloudPeakHour && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#c4b5fd' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                    Voile de cirrus / nuages hauts possible vers <strong>{highCloudPeakHour.hour}</strong>
                    {typeof highCloudPeakHour.cloudCoverHigh === 'number' ? ` (${highCloudPeakHour.cloudCoverHigh}%)` : ''}.
                  </span>
                </li>
              )}
              {today.highCloudFilter && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#fbbf24' }} />
                  <span className="text-sm" style={{ color: '#fde68a' }}>
                    Voile de cirrus ({today.highCloudCover}%) — filtrage solaire réduisant la VZ.
                  </span>
                </li>
              )}
              {today.convergence && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#34d399' }} />
                  <span className="text-sm" style={{ color: '#6ee7b7' }}>Convergence détectée — lignes de nuages possibles.</span>
                </li>
              )}
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#f87171' }} />
                <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>
                  Risque orageux : <strong>{today.stormRisk}%</strong>
                  {today.stormRisk > 40 ? ' — surveillance accrue requise.' : today.stormRisk > 20 ? ' — faible à modéré.' : ' — négligeable.'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* ── SECTION 6: Analyse des Tendances (Les "Pourquoi") ── */}
        <div className="rounded-xl p-3 sm:p-4 mb-3 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} style={{ color: '#7ec8e3' }} />
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#7ec8e3' }}>Analyse des Tendances — Les "Pourquoi"</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {/* Pressure */}
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Gauge size={12} style={{ color: '#a78bfa' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#a78bfa' }}>Pression</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{pressureText}</p>
            </div>
            {/* Humidity */}
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.12)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Droplets size={12} style={{ color: '#34d399' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#34d399' }}>Humidité</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{humidityText}</p>
            </div>
            {/* Wind */}
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(126,200,227,0.05)', border: '1px solid rgba(126,200,227,0.12)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Wind size={12} style={{ color: '#7ec8e3' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#7ec8e3' }}>Vent</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{windText}</p>
            </div>
            {/* Rain */}
            <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(126,200,227,0.05)', border: '1px solid rgba(126,200,227,0.12)' }}>
              <div className="flex items-center gap-2 mb-1">
                <CloudRain size={12} style={{ color: todayRainProb >= 35 || todayRainMm >= 1 ? '#fbbf24' : '#34d399' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: todayRainProb >= 35 || todayRainMm >= 1 ? '#fbbf24' : '#34d399' }}>Pluie / averses</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{rainText}</p>
            </div>
            {/* Front passage */}
            {frontText && (
              <div className="rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(249,115,22,0.05)', border: '1px solid rgba(249,115,22,0.2)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={12} style={{ color: '#f97316' }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#f97316' }}>Signal indicatif</span>
                </div>
                <p className="text-sm" style={{ color: '#fed7aa' }}>{frontText}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── SECTION 7: Alerts ── */}
        {(today.stormRisk > 30 || today.overdevelopmentRisk || today.windShearFragmented || todayRainProb >= 35 || todayRainMm >= 1) && (
          <div className="rounded-xl p-4 sm:p-5 mb-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} style={{ color: 'var(--danger-red)' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Alertes & Précautions</h2>
            </div>
            <ul className="space-y-2">
              {today.stormRisk > 30 && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--danger-red)' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>Risque orageux de {today.stormRisk}% — retour terrain recommandé avant {today.convectionEnd}.</span>
                </li>
              )}
              {(todayRainProb >= 35 || todayRainMm >= 1) && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--info-blue)' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>Pluie ou averses possibles ({todayRainProb}%, {todayRainMm.toFixed(1)} mm) — surveiller le radar avant de sortir les planeurs.</span>
                </li>
              )}
              {today.overdevelopmentRisk && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--warning-orange)' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>Surdéveloppement convectif probable en fin d'après-midi.</span>
                </li>
              )}
              {today.windShearFragmented && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--warning-orange)' }} />
                  <span className="text-sm print-text" style={{ color: 'var(--text-secondary)' }}>Cisaillement de vent — thermiques fragmentés, centrage difficile.</span>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* ── SECTION 8: Tendances — Analyse Narrative Experte ── */}
        {nextDays.length > 0 && (
          <div className="rounded-xl p-4 sm:p-5 mb-4 print-card" style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Sun size={16} style={{ color: '#fbbf24' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#fbbf24' }}>Tendances — Analyse de la Semaine</h2>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Analyse narrative experte · Identifiez votre journée de vol optimale
            </p>

            {/* Best day highlight banner — "visez [Jour]" style */}
            {(() => {
              const allDays = [today, ...nextDays];
              let bestIdx = findBestDayIndex(allDays);
              const bestDay = allDays[bestIdx];
              // Only show if best day is a future day with a decent score
              if (bestIdx === 0 || bestDay.qualityScore < 15) return null;
              const hasInstableMass = detectScenario(bestDay, allDays[bestIdx - 1] ?? bestDay, allDays[bestIdx + 1] ?? null) === 'belle-journee';
              const bestDewpoint = deriveDewPoint(bestDay);
              const bestSpread = bestDay.tempMax - bestDewpoint;
              const massNote = hasInstableMass
                ? `masse d'air instable et plafond exploitable (${bestDay.maxCumulusBase} m AGL)`
                : `meilleures conditions de la semaine (score ${bestDay.qualityScore}/100)`;
              return (
                <div className="rounded-lg px-4 py-3 mb-4" style={{ backgroundColor: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)' }}>
                  <div className="flex items-start gap-3">
                    <Sun size={16} style={{ color: '#34d399', flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#34d399' }}>
                        Si vous devez poser un congé, visez <span className="underline">{bestDay.dayLabel}</span> : c'est la seule fenêtre avec une {massNote}.
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        Écart T/Td : {(bestDay.tempMax - bestDewpoint).toFixed(0)}°C · VZ max : {bestDay.maxVZ} m/s · Plafond : {bestDay.maxCumulusBase} m AGL
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-4">
              {nextDays.map((day, idx) => {
                const allDays = [today, ...nextDays];
                const allIdx = idx + 1; // position in full week array
                const ref = idx === 0 ? today : nextDays[idx - 1];
                const nextDay = idx < nextDays.length - 1 ? nextDays[idx + 1] : null;
                const isBestDay = findBestDayIndex(allDays) === allIdx;
                const scenario = detectScenario(day, ref, nextDay);
                const badge = scenarioBadge(scenario);
                const thermal = thermalType(day);
                const narrative = generateExpertNarrative(day, ref, nextDay, isBestDay, allDays, selectedAerodrome);
                const weekDetails = weeklyTrendDetails(day);
                const dayQNH = deriveQNH(day);
                const dayVis = deriveVisibility(day);
                const dayDew = deriveDewPoint(day);
                const daySpread = day.tempMax - dayDew;
                const dayRainProb = rainProbability(day);
                const dayRainMm = rainTotalMm(day);
                const plafTrend = ceilingTrend(ref, day);
                const isHangar = scenario === 'hangar';
                const dayColor = isHangar ? '#94a3b8'
                  : day.verdict === 'favorable' ? '#34d399'
                  : day.verdict === 'marginal' ? '#fbbf24'
                  : day.verdict === 'storm-risk' ? '#f97316' : '#f87171';

                // Derive sunrise/sunset for future days (approximate from today's values with small offset)
                const daySunrise = day.sunrise ?? today.sunrise;
                const daySunset = day.sunset ?? today.sunset;

                // Overdevelopment warning for this day
                const hasOverdevRisk = daySpread < 3;
                // Wind choppy warning
                const hasChoppyWind = day.windSurface.speed > 25;
                // Pressure tendency warning
                const prevQNHDay = deriveQNH(ref);
                const qnhDeltaDay = prevQNHDay - dayQNH;
                const hasQNHDrop = qnhDeltaDay > 3;

                return (
                  <div
                    key={day.date}
                    className="rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: isHangar ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.025)',
                      border: isHangar
                        ? '1px solid rgba(148,163,184,0.2)'
                        : isBestDay ? '1px solid rgba(52,211,153,0.4)' : '1px solid var(--card-border)',
                      boxShadow: isBestDay ? '0 0 0 1px rgba(52,211,153,0.15)' : 'none',
                      opacity: isHangar ? 0.8 : 1,
                    }}
                  >
                    {/* Card header */}
                    <div className="flex items-center justify-between px-4 py-3 gap-2 flex-wrap" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-bold capitalize" style={{ color: isHangar ? '#94a3b8' : 'var(--text-primary)' }}>
                          {isHangar ? `Journée non propice — ${day.dayLabel}` : day.dayLabel}
                        </span>
                        {isBestDay && !isHangar && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(52,211,153,0.2)', color: '#34d399' }}>
                            ★ Meilleure journée
                          </span>
                        )}
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: badge.bg, color: badge.color }}>
                          {badge.label}
                        </span>
                      </div>
                      {/* Score badge */}
                      <span className="text-sm font-bold font-mono px-2.5 py-1 rounded-lg flex-shrink-0" style={{ backgroundColor: `${dayColor}18`, color: dayColor, border: `1px solid ${dayColor}40` }}>
                        {day.qualityScore}/100
                      </span>
                    </div>

                    {/* Narrative — main content */}
                    <div className="px-4 pt-3 pb-2">
                      <p className="text-sm leading-relaxed" style={{ color: isHangar ? '#94a3b8' : 'var(--text-secondary)', lineHeight: '1.7' }}>
                        {narrative}
                      </p>
                      {weekDetails && (
                        <p className="text-xs mt-2 leading-relaxed" style={{ color: isHangar ? '#7c8797' : '#8bd7ff' }}>
                          {weekDetails}
                        </p>
                      )}
                    </div>

                    {/* Cause-and-effect warning pills (only for non-hangar days) */}
                    {!isHangar && (hasOverdevRisk || hasChoppyWind || hasQNHDrop || dayRainProb >= 35 || dayRainMm >= 1) && (
                      <div className="px-4 pb-2 flex flex-wrap gap-2">
                        {hasOverdevRisk && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)' }}>
                            ⚡ Overdevelopment — écart T/Td {daySpread.toFixed(0)}°C
                          </span>
                        )}
                        {hasChoppyWind && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>
                            💨 Thermiques hachés — {day.windSurface.speed} km/h
                          </span>
                        )}
                        {hasQNHDrop && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                            ▼ Tendance pression -{qnhDeltaDay} - a confirmer
                          </span>
                        )}
                      </div>
                    )}

                    {/* Thermal type pill (hidden for hangar days) */}
                    {!isHangar && (
                      <div className="px-4 pb-3">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                          style={{
                            backgroundColor: thermal === 'bleu' ? 'rgba(126,200,227,0.1)' : thermal === 'cumulus' ? 'rgba(167,139,250,0.1)' : 'rgba(100,116,139,0.1)',
                            color: thermal === 'bleu' ? '#7ec8e3' : thermal === 'cumulus' ? '#a78bfa' : '#64748b',
                            border: `1px solid ${thermal === 'bleu' ? 'rgba(126,200,227,0.25)' : thermal === 'cumulus' ? 'rgba(167,139,250,0.25)' : 'rgba(100,116,139,0.2)'}`,
                          }}
                        >
                          {thermal === 'bleu' ? '○ Thermiques purs (sans cumulus)' : thermal === 'cumulus' ? '☁ Cumulus à base haute' : '— Pas de thermiques'}
                        </span>
                      </div>
                    )}

                    {/* Compact metrics row — includes sunrise/sunset + dew point */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-4 py-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(0,0,0,0.22)' }}>
                      {/* Sunrise */}
                      <div className="flex items-center gap-1.5">
                        <Sunrise size={11} style={{ color: '#fbbf24' }} />
                        <span className="text-xs font-mono" style={{ color: '#fbbf24' }}>{daySunrise}</span>
                      </div>
                      {/* Sunset */}
                      <div className="flex items-center gap-1.5">
                        <Sunset size={11} style={{ color: '#f97316' }} />
                        <span className="text-xs font-mono" style={{ color: '#f97316' }}>{daySunset}</span>
                      </div>
                      {/* Dew point + spread */}
                      <div className="flex items-center gap-1.5">
                        <Droplets size={11} style={{ color: '#34d399' }} />
                        <span className="text-xs" style={{ color: '#b7c7d8' }}>Td</span>
                        <span className="text-xs font-bold font-mono" style={{ color: '#34d399' }}>{dayDew}°C</span>
                        <span className="text-xs" style={{ color: daySpread < 3 ? '#fb923c' : daySpread < 6 ? '#facc15' : '#b7c7d8' }}>
                          (écart {daySpread.toFixed(0)}°C)
                        </span>
                      </div>
                      {!isHangar && (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Gauge size={11} style={{ color: '#a78bfa' }} />
                            <span className="text-xs" style={{ color: '#b7c7d8' }}>Indice pression</span>
                            <span className="text-xs font-bold font-mono" style={{ color: '#a78bfa' }}>{dayQNH}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Eye size={11} style={{ color: '#7ec8e3' }} />
                            <span className="text-xs" style={{ color: '#b7c7d8' }}>Vis.</span>
                            <span className="text-xs font-bold font-mono" style={{ color: '#7ec8e3' }}>{dayVis} km</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs" style={{ color: '#b7c7d8' }}>Plafond</span>
                            <span className="inline-flex items-center gap-1">
                              <TrendIcon trend={plafTrend} />
                              <span className="text-xs font-bold font-mono" style={{ color: 'var(--text-secondary)' }}>
                                {day.maxCumulusBase > 0 ? `${day.maxCumulusBase} m` : '--'}
                              </span>
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Zap size={11} style={{ color: '#fbbf24' }} />
                            <span className="text-xs" style={{ color: '#b7c7d8' }}>VZ max</span>
                            <span className="text-xs font-bold font-mono" style={{ color: '#fbbf24' }}>{day.maxVZ} m/s</span>
                          </div>
                          {day.stormRisk > 30 && (
                            <div className="flex items-center gap-1.5">
                              <AlertTriangle size={11} style={{ color: '#f87171' }} />
                              <span className="text-xs font-mono" style={{ color: '#f87171' }}>Orages {day.stormRisk}%</span>
                            </div>
                          )}
                          {(dayRainProb >= 35 || dayRainMm >= 1) && (
                            <div className="flex items-center gap-1.5">
                              <CloudRain size={11} style={{ color: '#7ec8e3' }} />
                              <span className="text-xs font-mono" style={{ color: '#7ec8e3' }}>Averses {dayRainProb}%</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="rounded-xl px-4 sm:px-5 py-4 text-xs" style={{ backgroundColor: 'rgba(126,200,227,0.04)', border: '1px solid rgba(126,200,227,0.1)', color: 'var(--text-muted)' }}>
          Ce briefing est généré à titre indicatif à partir des données {today?.modelSourceLabel ?? 'Open-Meteo'}. Le pilote reste seul
          responsable de la décision de vol. Application développée par Pascal Mourgues — SoarCast v1.0.21 bis.
        </div>
      </div>
    </>
  );
}
